import type { AccountView, Member } from '../api.ts'
import { useAuth } from '../lib/auth.tsx'
import { fmt } from '../lib/format.ts'
import type { Screen } from '../screens.ts'
import { SCREENS } from '../screens.ts'

interface SidebarProps {
  screen: Screen
  onNavigate: (screen: Screen) => void
  accounts: AccountView[]
  members: Member[]
  badges: Partial<Record<Screen, string>>
}

/**
 * The accounts, grouped the way you think about them rather than by YNAB's type list.
 * Derived rather than stored, so a new account in YNAB never needs a heading picked.
 */
const GROUPS: { name: string; types: string[] }[] = [
  { name: 'Cash', types: ['checking', 'savings', 'cash'] },
  { name: 'Credit', types: ['creditCard', 'lineOfCredit'] },
  {
    name: 'Loans',
    types: [
      'mortgage',
      'autoLoan',
      'studentLoan',
      'personalLoan',
      'medicalDebt',
      'otherDebt',
      'otherLiability',
    ],
  },
  { name: 'Tracking', types: ['otherAsset'] },
]

export function Sidebar({ screen, onNavigate, accounts, members, badges }: SidebarProps) {
  const { signedIn, signOut } = useAuth()
  const groups = GROUPS.map((group) => {
    const list = accounts.filter((account) => group.types.includes(account.type))
    return { ...group, accounts: list, total: list.reduce((sum, a) => sum + a.balance, 0) }
  }).filter((group) => group.accounts.length > 0)

  return (
    /*
     * Sticky, so the nav and the balances stay put down a long page. `self-start` is
     * the load-bearing part: a flex child stretches to the row's full height by
     * default, and an element as tall as its scroll container can never stick.
     *
     * Gone below `lg`, where `MobileNav` takes over.
     */
    <aside className="sticky top-0 hidden h-screen w-[236px] flex-none flex-col gap-[26px] self-start overflow-y-auto bg-sand px-[14px] pt-[22px] pb-[18px] lg:flex">
      <div className="flex flex-col gap-0.5 px-2">
        <div className="font-display text-[27px] leading-none tracking-[-0.01em]">Kete</div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted">Beside YNAB</div>
      </div>

      <nav className="flex flex-col gap-0.5">
        {SCREENS.map((item) => {
          const active = item.id === screen
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onNavigate(item.id)}
              aria-current={active ? 'page' : undefined}
              className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-[14.5px] text-ink transition-colors ${
                active
                  ? 'bg-card font-medium shadow-[var(--shadow-raise)]'
                  : 'bg-transparent font-light hover:bg-card/60'
              }`}
            >
              <span>{item.label}</span>
              {badges[item.id] && (
                <span className="text-[11px] tracking-[0.04em] text-muted">{badges[item.id]}</span>
              )}
            </button>
          )
        })}
      </nav>

      <div className="flex flex-col gap-2.5 px-2">
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted">In YNAB</div>
        {groups.map((group) => (
          <div key={group.name} className="flex flex-col gap-[5px] pb-2">
            <div className="flex justify-between text-[11px] uppercase tracking-[0.1em] text-ink/55">
              <span>{group.name}</span>
              <span>{fmt(group.total)}</span>
            </div>
            {group.accounts.map((account) => (
              // A link out, not a screen here: an account's register is YNAB's job.
              <a
                key={account.id}
                href={account.url ?? undefined}
                target="_blank"
                rel="noreferrer"
                title={`Open ${account.name} in YNAB`}
                className="-mx-1.5 flex justify-between rounded-md px-1.5 py-0.5 text-[13px] text-ink-soft no-underline transition-colors hover:bg-card/60"
              >
                <span className="max-w-[120px] truncate">
                  {account.importInError && (
                    <span className="text-blush-deep" title="Bank connection in error">
                      ●{' '}
                    </span>
                  )}
                  {account.name}
                </span>
                <span className={account.balance < 0 ? 'text-blush-deep' : undefined}>
                  {fmt(account.balance)}
                </span>
              </a>
            ))}
          </div>
        ))}
      </div>

      <div className="mt-auto flex items-center gap-2 border-t border-line-warm px-2 pt-2.5">
        {members.map((member, index) => (
          <div
            key={member.id}
            title={member.name}
            style={{ background: member.colour, marginLeft: index === 0 ? 0 : -14 }}
            className="flex h-[26px] w-[26px] items-center justify-center rounded-full text-[11px] font-medium text-on-member"
          >
            {member.initials}
          </div>
        ))}
        <div className="ml-1.5 truncate text-[12px] text-muted">
          {members.map((m) => m.name).join(' & ')}
        </div>
        {signedIn && (
          <button
            type="button"
            onClick={signOut}
            title="Sign out"
            className="ml-auto flex-none rounded-full px-2 py-1 text-[11px] text-muted transition-colors hover:bg-sand hover:text-ink"
          >
            Sign out
          </button>
        )}
      </div>
    </aside>
  )
}
